<template>
  <section class="how" ref="root">
    <div class="how__inner" :class="{ 'how__inner_visible': inView }">
      <h2 class="how__headline">Как это работает</h2>
      <ul class="how__steps">
        <li
          class="how__step"
          v-for="(step, i) in steps"
          :key="step"
          :style="{ transitionDelay: `${0.08 + i * 0.08}s` }"
        >
          <span class="how__num">{{ i + 1 }}</span>
          <span class="how__line" v-if="i < steps.length - 1" aria-hidden="true" />
          <p class="how__text">{{ step }}</p>
        </li>
      </ul>
      <p class="how__outro">
        Это не магия.<br />
        <strong>Это психология + визуальная среда.</strong>
      </p>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
  name: 'HomeHowItWorks',
  setup() {
    const root = ref(null)
    const inView = ref(false)
    let observer = null

    const steps = [
      'Ты видишь образ каждый день',
      'Подсознание фиксирует фокус',
      'Меняется мышление',
      'Меняются действия',
      'Растёт результат'
    ]

    onMounted(() => {
      observer = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) inView.value = true },
        { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
      )
      if (root.value) observer.observe(root.value)
    })
    onUnmounted(() => observer?.disconnect())

    return { root, inView, steps }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$how-duration: 0.6s;
$how-ease: cubic-bezier(0.22, 1, 0.36, 1);

.how {
  position: relative;
  padding: 100px $gap 100px;
  background: $color-bg;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-image:
      radial-gradient(ellipse 80% 50% at 50% 100%, rgba(0, 0, 0, 0.04) 0%, transparent 50%),
      url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.06'%3E%3Ccircle cx='4' cy='4' r='1.5'/%3E%3Ccircle cx='20' cy='4' r='1.5'/%3E%3Ccircle cx='4' cy='20' r='1.5'/%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E");
  }

  @media (max-width: $breakpoint-mobile) {
    padding: 64px $gap 72px;
  }

  &__inner {
    position: relative;
    z-index: 1;
    max-width: $container-max;
    margin: 0 auto;

    &_visible .how__headline { opacity: 1; transform: translateY(0); }
    &_visible .how__step { opacity: 1; transform: translateX(0); }
    &_visible .how__outro { opacity: 1; transform: translateY(0); }
  }

  &__headline {
    font-size: clamp(14px, 1.5vw, 16px);
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: $color-text-muted;
    margin: 0 0 48px;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $how-duration $how-ease, transform $how-duration $how-ease;
  }

  &__steps {
    list-style: none;
    margin: 0 0 56px;
    padding: 0;
    max-width: 560px;

    @media (max-width: $breakpoint-mobile) {
      margin-bottom: 48px;
    }
  }

  &__step {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 24px;
    padding: 20px 0;
    opacity: 0;
    transform: translateX(-20px);
    transition: opacity $how-duration $how-ease, transform $how-duration $how-ease;

    @media (max-width: $breakpoint-mobile) {
      gap: 18px;
      padding: 16px 0;
    }
  }

  &__num {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: $color-primary;
    color: $color-primary-inverse;
    font-size: 18px;
    font-weight: 800;
    border-radius: 50%;

    @media (max-width: $breakpoint-mobile) {
      width: 38px;
      height: 38px;
      font-size: 16px;
    }
  }

  &__line {
    position: absolute;
    left: 21px;
    top: 64px;
    bottom: -20px;
    width: 2px;
    background: linear-gradient(to bottom, $color-border 0%, transparent 100%);

    @media (max-width: $breakpoint-mobile) {
      left: 18px;
      top: 54px;
      bottom: -16px;
    }
  }

  &__text {
    font-size: clamp(17px, 1.8vw, 20px);
    font-weight: 600;
    line-height: 1.4;
    color: $color-text;
    margin: 0;
    padding-top: 8px;
  }

  &__outro {
    font-size: clamp(17px, 1.8vw, 20px);
    line-height: 1.5;
    color: $color-text-muted;
    margin: 0;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $how-duration $how-ease 0.3s, transform $how-duration $how-ease 0.3s;

    strong {
      display: block;
      margin-top: 8px;
      font-size: clamp(20px, 2.4vw, 24px);
      font-weight: 800;
      color: $color-text;
    }
  }
}
</style>
